import { useMemo, useRef } from "react";
import { Grid, Typography, Tooltip, Theme } from "@mui/material";
import { IBidPackage, IPairingPosition } from "@vmosolutions/oc-interfaces";
import { PairingPositionCell } from "src/components/FilterSearchTable/CustomCellComponents/PairingPositionCell";
import WarningIcon from "@mui/icons-material/Warning";
import { BidPackageTimeScaleCanvas } from "./BidPackageTimeScaleCanvas";
import { timeScales } from "src/pages/FlightTimeline/TimelineUtils";

interface IBidPackageSummaryProps {
  id: string;
  path: string[];
  bidPackage: IBidPackage;
  bidPairingCount: number;
  crewCount: number;
  hideTimeScale?: boolean;
}

export interface IGroupOption {
  value: string;
  key: string;
  label: string;
}

const defaultStart = new Date()?.valueOf() - timeScales.day;
const defaultEnd = new Date()?.valueOf() + timeScales.month + timeScales.day;

/**
 * Display Bid Package Summary
 * @param props -
 */
export const BidPackageSummary = (props: IBidPackageSummaryProps): JSX.Element => {
  const { id, path, bidPackage, bidPairingCount, crewCount, hideTimeScale } = props;
  const { _id, title, positions, isDisabled, nonFlyingAssignments } = bidPackage;

  const timeScaleRef = useRef<HTMLDivElement>(null);

  // Assignment Positions on this bid package.
  const pairingPositions: IPairingPosition[] = useMemo(
    () => positions?.map((posTitle) => ({ position: posTitle })),
    [positions]
  );

  // Time Scale Display
  const timeScaleStart = useMemo(() => {
    return bidPackage?.bidWindowOpen ? new Date(bidPackage?.bidWindowOpen)?.valueOf() - timeScales.day : defaultStart;
  }, [bidPackage?.bidWindowOpen]);

  const timeScaleEnd = useMemo(() => {
    return bidPackage?.bidBlockEnd ? new Date(bidPackage?.bidBlockEnd)?.valueOf() + timeScales.day : defaultEnd;
  }, [bidPackage?.bidBlockEnd]);

  const currentContentRef = timeScaleRef?.current;
  const timeScaleWidth = useMemo(() => currentContentRef?.getBoundingClientRect()?.width ?? 0, [currentContentRef]);

  // Bid Window style and position
  const bidWindowStyle = useMemo(() => {
    const isVisible = !!bidPackage?.bidWindowOpen && !!bidPackage?.bidWindowClose;
    const windowOpen = new Date(bidPackage?.bidWindowOpen)?.valueOf();
    const windowClose = new Date(bidPackage?.bidWindowClose)?.valueOf();
    const widthPixel = (timeScaleWidth * (windowClose - windowOpen)) / (timeScaleEnd - timeScaleStart);
    const leftPixel = (timeScaleWidth * (windowOpen - timeScaleStart)) / (timeScaleEnd - timeScaleStart);
    const leftPercentage = (leftPixel * 100) / timeScaleWidth;
    // console.log(blockWidthPixel, blockLeftPercentage);
    return {
      display: isVisible ? "block" : "none",
      width: isNaN(widthPixel) ? 0 : widthPixel,
      left: `${leftPercentage}%`,
      height: 20,
      backgroundColor: "purple",
      top: 50,
      index: 5000,
    };
  }, [timeScaleStart, timeScaleEnd, bidPackage, timeScaleWidth]);

  // Bid Block style and position
  const bidBlockStyle = useMemo(() => {
    const isVisible = !!bidPackage?.bidBlockStart && !!bidPackage?.bidBlockEnd;
    const blockStart = new Date(bidPackage?.bidBlockStart)?.valueOf();
    const blockEnd = new Date(bidPackage?.bidBlockEnd)?.valueOf();
    const widthPixel = (timeScaleWidth * (blockEnd - blockStart)) / (timeScaleEnd - timeScaleStart);
    const leftPixel = (timeScaleWidth * (blockStart - timeScaleStart)) / (timeScaleEnd - timeScaleStart);
    const leftPercentage = (leftPixel * 100) / timeScaleWidth;
    // console.log(blockWidthPixel, blockLeftPercentage);
    return {
      display: isVisible ? "block" : "none",
      width: isNaN(widthPixel) ? 0 : widthPixel,
      left: `${leftPercentage}%`,
      height: 20,
      backgroundColor: "teal",
      top: 50,
      index: 6000,
    };
  }, [timeScaleStart, timeScaleEnd, bidPackage, timeScaleWidth]);

  // Release To Crew style and position
  const releaseStyle = useMemo(() => {
    const isVisible = !!bidPackage?.releaseScheduleAt;
    const blockStart = new Date(bidPackage?.releaseScheduleAt)?.valueOf();
    const leftPixel = (timeScaleWidth * (blockStart - timeScaleStart)) / (timeScaleEnd - timeScaleStart);
    const leftPercentage = (leftPixel * 100) / timeScaleWidth;
    return {
      display: isVisible ? "block" : "none",
      width: 2,
      left: `${leftPercentage}%`,
      height: 20,
      backgroundColor: "yellow",
      top: 50,
      index: 7000,
    };
  }, [timeScaleStart, timeScaleEnd, bidPackage, timeScaleWidth]);

  const bidPackageSummaryStyles = (theme: Theme): any => ({
    BidPackageSummary: {
      position: "relative",
      borderRadius: "5px 5px 0px 0px",
      overflow: "hidden",
      transition: theme.transitions.create(["height"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.short,
      }),
      backgroundColor: "black",
      "&.crew_schedule": { backgroundColor: "transparent" },
      "&.open": {
        "& .toggle_bid_package_summary_btn svg": {
          transform: "rotate(180deg)",
        },
      },
      "& .toggle_bid_package_summary_btn": {
        position: "absolute",
        zIndex: 99,
        backgroundColor: "black",
        right: 0,
        borderRadius: 0,
        "&:hover": { backgroundColor: "#777" },
        "& svg": {
          transition: theme.transitions.create(["transform"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.short,
          }),
        },
      },

      // Bid Package Summary Styles
      "& .bidpackage_summary_disabled": { width: "auto", margin: "5px" },
      "& .bidpackage_summary_title": { width: "auto", margin: "5px" },
      "& .bidpackage_summary_positions": { width: "auto", margin: "5px" },
      "& .bidpackage_nfa_assignments": {
        width: "auto",
        margin: "5px",
        "& .nfa_badge": {
          height: "16px",
          borderRadius: "3px",
          padding: "0px 5px",
          marginRight: "10px",
          "& div": { color: "black", fontSize: 10, marginTop: "2px" },
        },
      },
      "& .time_display_wrapper": {
        width: "calc(100% - 10px)",
        height: "75px",
        position: "relative",
        margin: "auto 5px 5px 5px",
        "& .bidpackage_time_display_canvas": {
          width: "100%",
          height: "75px",
          minHeight: "0px",
          minWidth: "0px",
          flexBasis: "0px",
          overflow: "hidden",
          border: `1px solid ${theme.custom.forms.inputBorder}`,
          "& .timeDisplayGL": {
            width: "100%",
            height: "100%",
            minHeight: "0px",
            minWidth: "0px",
            flexBasis: "0px",
            overflow: "hidden",
            position: "relative",
          },
        },
      },
    },
  });

  return (
    <Grid
      sx={(theme) => bidPackageSummaryStyles(theme).BidPackageSummary}
      className={`${hideTimeScale ? "crew_schedule" : ""}`}
      container
      direction="column"
      wrap="nowrap"
      style={{ height: "100%", overflowY: "auto" }}
    >
      <Grid container alignItems="center">
        {isDisabled && (
          <Grid container className="bidpackage_summary_disabled">
            <WarningIcon color="secondary" fontSize="small" />
            <Typography variant="body2" color="secondary" style={{ fontSize: 16, fontWeight: 700, margin: "0px 4px" }}>
              DISABLED
            </Typography>
          </Grid>
        )}

        <Typography className="bidpackage_summary_title" color="textPrimary">
          {title}
        </Typography>

        <Grid container className="bidpackage_summary_positions" wrap="nowrap" alignItems="center">
          <Typography variant="caption" style={{ minWidth: 130 }}>
            Assignment Positions
          </Typography>
          <PairingPositionCell positions={pairingPositions ?? []} />
        </Grid>

        <Typography variant="caption" style={{ margin: 5 }}>
          {`Crew Count (${crewCount})`}
        </Typography>
        <Typography variant="caption" style={{ margin: 5 }}>
          {`Bid Pairing Count (${bidPairingCount})`}
        </Typography>

        <Grid container className="bidpackage_nfa_assignments" wrap="nowrap" alignItems="center">
          <Typography variant="caption" style={{ minWidth: 110 }}>
            NFA Types/Count
          </Typography>
          {nonFlyingAssignments?.map((nfa) => (
            <Tooltip
              key={`nfa_${nfa?.nfaType?._id}`}
              title={`${nfa?.nfaType?.description} (max assignment count: ${nfa?.maxAmount})}`}
            >
              <Grid item className="nfa_badge" style={{ backgroundColor: `#${nfa?.nfaType?.defaultColor}` }}>
                <div>{`${nfa?.nfaType?.title} ${nfa?.maxAmount?.toString()}x`}</div>
              </Grid>
            </Tooltip>
          ))}
        </Grid>
      </Grid>

      {!hideTimeScale && (
        <div ref={timeScaleRef} className="time_display_wrapper">
          <BidPackageTimeScaleCanvas id={id} path={path} timeScaleStart={timeScaleStart} timeScaleEnd={timeScaleEnd} />
          <div
            style={{
              position: "absolute",
              ...bidWindowStyle,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Typography variant="caption" color="textPrimary" style={{ marginLeft: 5 }}>
              Bid Window
            </Typography>
          </div>
          <div style={{ position: "absolute", ...bidBlockStyle }}>
            <Typography variant="caption" color="textPrimary" style={{ marginLeft: 5 }}>
              Bid Block
            </Typography>
          </div>
          <div style={{ position: "absolute", ...releaseStyle }} />
        </div>
      )}
    </Grid>
  );
};
